// Lightweight first-party analytics client. Sends events to the Flask
// backend at studio.speakroar.com. All transports use sendBeacon when
// available (survives page unload) and fall back to fetch with keepalive.
//
// Privacy note: we generate a random anonymous session ID stored in
// sessionStorage (cleared when the browser tab closes). No PII is sent
// from the client; the server resolves country/UA from request headers.

const TRACK_ENDPOINT =
  process.env.NEXT_PUBLIC_ANALYTICS_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  "https://studio.speakroar.com";

const SESSION_KEY = "sr_an_sid";
const LANDING_KEY = "sr_an_landing";
const UTM_KEY = "sr_an_utm";

export type TrackPayload = {
  session_id: string;
  event_type: string;
  path?: string;
  label?: string;
  value?: number;
  metadata?: Record<string, unknown>;
  referrer?: string;
  lang?: string;
  landing?: string;
  utm?: { source?: string; medium?: string; campaign?: string };
};

function randId(): string {
  // 96-bit random — collision-free for analytics purposes.
  const bytes = new Uint8Array(12);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return randId();
  }
}

function getLanding(): string {
  if (typeof window === "undefined") return "";
  try {
    let l = sessionStorage.getItem(LANDING_KEY);
    if (!l) {
      l = window.location.pathname + window.location.search;
      sessionStorage.setItem(LANDING_KEY, l);
    }
    return l;
  } catch {
    return window.location.pathname;
  }
}

function getUtm(): { source?: string; medium?: string; campaign?: string } {
  if (typeof window === "undefined") return {};
  try {
    const cached = sessionStorage.getItem(UTM_KEY);
    if (cached) return JSON.parse(cached);
    const sp = new URLSearchParams(window.location.search);
    const utm = {
      source: sp.get("utm_source") || sp.get("gad_source") || "",
      medium: sp.get("utm_medium") || "",
      campaign: sp.get("utm_campaign") || sp.get("gad_campaignid") || "",
    };
    sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    return utm;
  } catch {
    return {};
  }
}

function send(body: TrackPayload) {
  if (typeof window === "undefined") return;
  const url = `${TRACK_ENDPOINT}/api/analytics/track`;
  const data = JSON.stringify(body);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([data], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch {
    // fall through to fetch
  }
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: data,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // give up silently — analytics must never break the page
  }
}

export function track(event: string, extra?: Partial<TrackPayload>) {
  if (typeof window === "undefined") return;
  const sid = getSessionId();
  if (!sid) return;
  send({
    session_id: sid,
    event_type: event,
    path: window.location.pathname + window.location.search,
    referrer: document.referrer || "",
    lang: navigator.language || "",
    landing: getLanding(),
    utm: getUtm(),
    ...extra,
  });
}

export const trackPageview = (path?: string) =>
  track("pageview", { path: path ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : "") });

export const trackClick = (label: string, extra?: Record<string, unknown>) =>
  track("click", { label, metadata: extra });

export const trackEvent = (type: string, label?: string, value?: number, metadata?: Record<string, unknown>) =>
  track(type, { label, value, metadata });
